import React, { ReactNode, useEffect, useRef, useState } from 'react';
import Scrollbars from 'react-custom-scrollbars';

import classNames from 'classnames';

import UiSelectHeading from './ui-select-heading/ui-select-heading';
import UiSelectItem from './ui-select-item/ui-select-item';
import { SELECT_HEADING } from './constans';

import './ui-select.scss';

export type OptionType = {
  value: any;
  label: string | ReactNode;
  limit?: number;
};

interface UiSelectProps {
  disabled?: boolean;
  isMulti?: boolean;
  options: OptionType[];
  onChange: (val: OptionType | OptionType[] | undefined) => void;
  placeholder?: string;
  value?: OptionType | OptionType[];
  sm?: boolean;
  lg?: boolean;
  isOpen?: boolean;
  label?: string;
  additionalInfo?: React.ReactNode;
  title?: string;
}

const MAX_HEIGHT = 300;

const UiSelect: React.FC<UiSelectProps> = ({
  options,
  isOpen,
  placeholder,
  value,
  onChange,
  isMulti,
  sm,
  lg = false,
  label,
  disabled,
  additionalInfo,
  title,
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [isOpened, setIsOpened] = useState(isOpen ? isOpen : false);
  const [selected, setSelected] = useState<string | undefined>(
    !isMulti ? (value as OptionType)?.value : undefined
  );
  const [multiSelected, setMultiSelected] = useState<string[] | undefined>([]);

  useEffect(() => {
    if (isMulti) {
      setMultiSelected((value as OptionType[]).map((item) => item.value));
    } else {
      selected !== (value as OptionType)?.value &&
        setSelected((value as OptionType)?.value);
    }
  }, [value]);

  useEffect(() => {
    if (isOpened && wrapperRef.current) {
      wrapperRef.current.focus();
    }
  }, [isOpened]);

  useEffect(() => {
    const handleEscClick = (e: KeyboardEvent) => {
      if (
        !(
          wrapperRef.current && wrapperRef.current.contains(e.target as Node)
        ) &&
        e.code === 'Escape'
      ) {
        setIsOpened(false);
      }
    };

    document.addEventListener('keydown', handleEscClick);

    return () => {
      document.removeEventListener('keydown', handleEscClick);
    };
  }, [wrapperRef]);

  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      if (
        !(wrapperRef.current && wrapperRef.current.contains(e.target as Node))
      ) {
        setIsOpened(false);
      }
    };

    document.addEventListener('click', handleOutsideClick);
    return () => {
      document.removeEventListener('click', handleOutsideClick);
    };
  }, [wrapperRef]);

  const checkSelected = (id: string) => {
    if (isMulti) {
      return multiSelected?.includes(id);
    }
    return selected === id;
  };

  const toggleMultiState = (val: string) => {
    const contains = multiSelected?.includes(val);

    if (contains) {
      setMultiSelected((prevState) =>
        prevState?.filter((item) => item !== val)
      );
    } else {
      setMultiSelected((prevState) => prevState && [...prevState, val]);
    }
  };

  const onCheckboxItemChange = (val: string, checked: boolean) => {
    if (!checked) {
      setMultiSelected((prevState) => prevState && [...prevState, val]);
    } else {
      setMultiSelected((prevState) =>
        prevState?.filter((item) => item !== val)
      );
    }
  };

  const toggleSingleState = (val: string) => setSelected(val);

  const onChangeResultSelected = () => {
    if (isMulti) {
      const selectedMultiOptions = [] as OptionType[];
      multiSelected?.forEach((selectedItem) => {
        const foundOption = options.find(
          (option) => option.value === selectedItem
        );
        if (foundOption) {
          selectedMultiOptions.push(foundOption);
        }
      });
      onChange(selectedMultiOptions);
    } else {
      const foundItem = options.find((option) => option.value === selected);
      onChange(foundItem);
    }
  };

  useEffect(() => {
    onChangeResultSelected();
  }, [selected, multiSelected?.length]);

  const onItemToggleSelect = (val: string) => {
    if (isMulti) {
      toggleMultiState(val);
    } else {
      toggleSingleState(val);
      setIsOpened(false);
    }
  };

  const renderSelectList = () =>
    options.map((item) => (
      <UiSelectItem
        sm={sm}
        lg={lg}
        key={item.value}
        onCheckboxItemChange={onCheckboxItemChange}
        isMulti={!!isMulti}
        selected={!!checkSelected(item.value)}
        onToggleSelect={onItemToggleSelect}
        value={item.value}
        label={item.label}
      />
    ));

  const getHeadingValue = () => {
    if (isMulti && multiSelected && multiSelected?.length > 0) {
      const item = options.find((option) => option.value === multiSelected[0]);
      return (
        (item?.label || '') +
        (multiSelected.length > 1 ? ` +${multiSelected.length - 1}` : '')
      );
    }
    if (title) {
      return title;
    }
    if (!isMulti) {
      return options.find((option) => option.value === selected)?.label;
    }
    return undefined;
  };

  const isTopSelectList =
    wrapperRef.current &&
    window.innerHeight - wrapperRef.current?.getBoundingClientRect().y >
      MAX_HEIGHT;

  const getHeading = () => {
    const headingValue = getHeadingValue();
    if (headingValue) return headingValue ?? '';
    if (placeholder) return placeholder ?? '';
    if (label) return '';
    return SELECT_HEADING;
  };

  return (
    <div className="ui-select-wrapper" ref={wrapperRef}>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */}
      <div
        className={classNames('ui-select-container', {
          'ui-select-container--focused': isOpened,
          'ui-select-container--sm': sm,
          'ui-select-container--lg': lg,
          'ui-select-container--disabled': disabled,
        })}
        onClick={() => !disabled && setIsOpened((prevState) => !prevState)}
      >
        <UiSelectHeading
          sm={sm}
          lg={lg}
          focused={isOpened}
          completed={!!selected}
          title={getHeading()}
          label={label}
          isOpened={isOpened}
          additionalInfo={additionalInfo}
        />
      </div>
      {isOpened && (
        <div
          role="list"
          className={`ui-select__list ${
            !isTopSelectList ? 'ui-select__list__top' : ''
          }`}
        >
          <Scrollbars autoHeight autoHeightMax={MAX_HEIGHT}>
            {renderSelectList()}
          </Scrollbars>
        </div>
      )}
    </div>
  );
};

export default UiSelect;
